// @catalog F.030
// Vistas del subnav de /margenes (Resumen / Distribución / Top margen / Críticos).
// Extraídas de MarginsLevel1.tsx para respetar P7 (≤500 líneas/archivo). Reciben
// los datos ya derivados desde el orquestador; no calculan métricas.
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MarginsService, {
  type MonthlyMarginsResponse,
  type StoreMarginMetrics,
  type TopMarginProduct,
} from "../../services/margins.service";
import { MarginsTimeline } from "./MarginsTimeline";
import { StoreMarginAnalysisTable } from "./StoreMarginAnalysisTable";
import { TopMarginProductsTable } from "./TopMarginProductsTable";
import { BottomMarginProductsTable } from "./BottomMarginProductsTable";
import { RankingBars, type RankingItem } from "../analytics/RankingBars";
import { WaterfallChart } from "../analytics/WaterfallChart";
import { ErosionBreakdown } from "../analytics/ErosionBreakdown";
import {
  MarginDistribution,
  type ScatterProduct,
} from "../analytics/MarginDistribution";

const CARD_SX = {
  backgroundColor: "var(--surface)",
  border: "1px solid var(--border)",
  boxShadow: "none",
} as const;

export function ResumenView(props: {
  monthlyData: MonthlyMarginsResponse[];
  selectedMonth: string | null;
  onMonthSelect: (key: string) => void;
  multiMonth: boolean;
  revenue: number;
  discounts: number;
  cost: number;
  marginAmount: number;
  opMarginRank: RankingItem<StoreMarginMetrics>[];
  onDrillStore: (s: StoreMarginMetrics) => void;
  scatterProducts: ScatterProduct[];
}): JSX.Element {
  const {
    monthlyData,
    selectedMonth,
    onMonthSelect,
    multiMonth,
    revenue,
    discounts,
    cost,
    marginAmount,
    opMarginRank,
    onDrillStore,
    scatterProducts,
  } = props;
  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "3fr 2fr" },
          gap: 2,
        }}
      >
        <MarginsTimeline
          data={monthlyData}
          selectedMonth={selectedMonth}
          onMonthSelect={onMonthSelect}
        />
        <Card sx={CARD_SX}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Waterfall del {multiMonth ? "período" : "mes"}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "var(--ink-3)", display: "block", mb: 2 }}
            >
              De venta a precio lista a margen real (ingreso − costo, con IVA)
            </Typography>
            {/* El descuento ya está embebido en el ingreso neto (Odoo
                price_subtotal_incl). Por eso el waterfall arranca en precio
                lista y resta el descuento UNA sola vez (lista → neto);
                restarlo del margen sería doble conteo (BUG-005). El margen
                real = ingreso − costo (D5: la comisión se expone aparte en
                su propio KPI, no reduce el margen que reconcilia con
                bancos). */}
            <WaterfallChart
              steps={[
                {
                  label: "Venta a precio lista",
                  value: revenue + discounts,
                  type: "start",
                  tooltip:
                    "Facturación del mes a precio de catálogo, antes de descuentos (= ingreso neto + descuentos). Fuente: ventas POS + vendedores en Odoo 18, con IVA. Es la primera barra del waterfall (la base del cálculo).",
                },
                {
                  label: "Descuentos",
                  value: -discounts,
                  type: "neg",
                  tooltip:
                    "Rebajas aplicadas sobre el precio de lista en el mes. Fuente: Odoo 18 (agregado mensual de descuentos). Se restan una sola vez para pasar del precio de lista a la facturación neta real.",
                },
                {
                  label: "Costo de venta",
                  value: -cost,
                  type: "neg",
                  tooltip:
                    "Costo de la mercancía vendida del mes, con IVA = cantidad vendida × costo unitario (tabla real_cost). POS directo; ventas SO prorrateadas por cobros reconciliados. Se resta del ingreso neto.",
                },
                {
                  label: "Margen real",
                  value: 0,
                  type: "end",
                  actualValue: marginAmount,
                  tooltip:
                    "Resultado = ingreso neto − costo (ambos con IVA). NO resta comisiones (van en su propio KPI) ni vuelve a descontar el descuento, que ya está embebido en el ingreso neto. Es el margen que reconcilia con bancos.",
                },
              ]}
              formatValue={MarginsService.formatCurrency}
            />
          </CardContent>
        </Card>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
        }}
      >
        <Card sx={CARD_SX}>
          <CardContent>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Margen % por punto de venta
              </Typography>
              <Tooltip
                title="Margen porcentual promedio del punto de venta en el período: (ingreso − costo) ÷ ingreso × 100, con IVA, promediado sobre sus ventas. Incluye tiendas POS y vendedores (SO); ordenado de mayor a menor margen."
                arrow
                placement="top"
              >
                <InfoOutlinedIcon
                  sx={{ fontSize: 15, color: "var(--ink-3)", cursor: "help" }}
                />
              </Tooltip>
            </Stack>
            <Typography
              variant="caption"
              sx={{ color: "var(--ink-3)", display: "block", mb: 2 }}
            >
              Tiendas POS + vendedores · ordenado por margen
            </Typography>
            <RankingBars
              items={opMarginRank}
              formatValue={(v) => `${v.toFixed(1)}%`}
              onSelect={(it) => it.raw && onDrillStore(it.raw)}
              emptyMessage="Sin datos de tiendas"
            />
          </CardContent>
        </Card>

        <Card sx={CARD_SX}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Descomposición de erosión
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "var(--ink-3)", display: "block", mb: 2 }}
            >
              Dónde se está yendo la ganancia
            </Typography>
            <ErosionBreakdown
              ventaNeta={revenue}
              costo={cost}
              descuentos={discounts}
              formatValue={MarginsService.formatCurrency}
            />
          </CardContent>
        </Card>
      </Box>

      <Card sx={CARD_SX}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Distribución de productos por margen real
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "var(--ink-3)", display: "block", mb: 2 }}
          >
            Cada burbuja = un producto · tamaño = margen total $ · cuadrante
            superior derecho = estrellas
          </Typography>
          <MarginDistribution
            products={scatterProducts}
            height={340}
            formatValue={MarginsService.formatCurrency}
          />
        </CardContent>
      </Card>
    </Stack>
  );
}

export function DistribucionView(props: {
  scatterProducts: ScatterProduct[];
}): JSX.Element {
  const { scatterProducts } = props;
  return (
    <Card sx={CARD_SX}>
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Mapa de productos por margen y volumen
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "var(--ink-3)", display: "block", mb: 2 }}
        >
          Identificá estrellas (alto volumen + alto margen) y productos que
          erosionan (alto volumen + bajo margen)
        </Typography>
        <MarginDistribution
          products={scatterProducts}
          height={480}
          formatValue={MarginsService.formatCurrency}
        />
      </CardContent>
    </Card>
  );
}

export function ProductosView(props: {
  stores: StoreMarginMetrics[];
  products: TopMarginProduct[];
  onProductClick: (productId: number, productName: string) => void;
  fetching: boolean;
}): JSX.Element {
  const { stores, products, onProductClick, fetching } = props;
  return (
    <Card sx={CARD_SX}>
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
          sx={{ mb: 1 }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Top productos por margen total
            </Typography>
            <Typography variant="caption" sx={{ color: "var(--ink-3)" }}>
              El indicador clave para gerencia: dónde está la ganancia real
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: "var(--ink-3)" }}>
              {stores.length} tiendas analizadas
            </Typography>
          </Box>
        </Stack>
        {stores.length === 0 ? (
          <Alert severity="info">
            No hay datos de tiendas para el rango seleccionado
          </Alert>
        ) : (
          <Box sx={{ mt: 2 }}>
            <StoreMarginAnalysisTable stores={stores} />
          </Box>
        )}
        <Box sx={{ mt: 3 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 1, color: "var(--ink-2)" }}
          >
            Detalle por producto
          </Typography>
          <TopMarginProductsTable
            products={products}
            onProductClick={onProductClick}
          />
        </Box>
        {fetching && (
          <Stack direction="row" justifyContent="center" sx={{ py: 2 }}>
            <CircularProgress size={20} />
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export function CriticosView(props: {
  products: TopMarginProduct[];
  onProductClick: (productId: number, productName: string) => void;
}): JSX.Element {
  const { products, onProductClick } = props;
  return (
    <Card sx={CARD_SX}>
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Productos con margen crítico
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "var(--ink-3)", display: "block", mb: 2 }}
        >
          Margen real bajo 22% o erosión severa por descuentos/comisiones
        </Typography>
        <BottomMarginProductsTable
          products={products}
          onProductClick={onProductClick}
        />
      </CardContent>
    </Card>
  );
}
